import { useRef, useEffect } from 'react';

const StarField = ({ starCount = 200, speed = 0.3 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let stars = [];

    const resize = () => {
      canvas.width = canvas.parentElement.offsetWidth;
      canvas.height = canvas.parentElement.offsetHeight;
    };

    const initStars = () => {
      stars = [];
      for (let i = 0; i < starCount; i++) {
        stars.push({
          x: Math.random() * canvas.width - canvas.width / 2,
          y: Math.random() * canvas.height - canvas.height / 2,
          z: Math.random() * 1000,
          prevX: 0,
          prevY: 0,
        });
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const cx = canvas.width / 2;
      const cy = canvas.height / 2;

      for (let i = 0; i < stars.length; i++) {
        const star = stars[i];
        star.z -= speed * 2;

        if (star.z <= 0) {
          star.x = Math.random() * canvas.width - cx;
          star.y = Math.random() * canvas.height - cy;
          star.z = 1000;
          star.prevX = 0;
          star.prevY = 0;
          continue;
        }

        const sx = (star.x / star.z) * 400 + cx;
        const sy = (star.y / star.z) * 400 + cy;

        if (sx < 0 || sx > canvas.width || sy < 0 || sy > canvas.height) {
          star.x = Math.random() * canvas.width - cx;
          star.y = Math.random() * canvas.height - cy;
          star.z = 1000;
          star.prevX = 0;
          star.prevY = 0;
          continue;
        }

        const depth = 1 - star.z / 1000;
        const size = depth * 2.5;
        const alpha = depth * 0.9;

        if (star.prevX !== 0 && star.prevY !== 0 && depth > 0.3) {
          ctx.beginPath();
          ctx.moveTo(star.prevX, star.prevY);
          ctx.lineTo(sx, sy);
          ctx.strokeStyle = `rgba(168, 67, 151, ${alpha * 0.3})`;
          ctx.lineWidth = size * 0.5;
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.arc(sx, sy, size, 0, Math.PI * 2);

        if (depth > 0.6) {
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        } else if (depth > 0.3) {
          ctx.fillStyle = `rgba(210, 60, 179, ${alpha * 0.8})`;
        } else {
          ctx.fillStyle = `rgba(168, 67, 151, ${alpha * 0.5})`;
        }
        ctx.fill();

        if (depth > 0.7) {
          ctx.beginPath();
          ctx.arc(sx, sy, size * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(168, 67, 151, ${alpha * 0.08})`;
          ctx.fill();
        }

        star.prevX = sx;
        star.prevY = sy;
      }

      animationId = requestAnimationFrame(animate);
    };

    resize();
    initStars();
    animate();

    const handleResize = () => { resize(); initStars(); };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, [starCount, speed]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};

export default StarField;
